import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useRouter } from 'next/router';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';

import Button from '@jobcase/react_pattern_library/components/Button';
import Input from '@jobcase/react_pattern_library/components/Input';
import LinkTypography from '@jobcase/react_pattern_library/components/LinkTypography';
import Spinner from '@jobcase/react_pattern_library/components/Spinner';
import styles from '@jobcase/react_pattern_library/styles/rebrand';

import { getArrivalEmail, getIsLoginLoader, isProfileClaimed, isProfileKnown } from '../../redux/nodes/authentication/selectors';
import { routeTo } from '../../redux/nodes/router/actions';
import actions, { attemptLogin } from '../../redux/nodes/authentication/actions';

import { getArrivalKey } from '../../utils/cookie';
import { isValidEmail } from '../../utils/validation';
import {
  reportJobcaseLoginNativeAuthClick,
  reportJobcaseLoginNativeAuthError,
  reportJobcaseSsoViewRendered,
  AUTH_ERROR_REASON,
  SSO_PAGE,
} from '../../utils/reporting/ssoEvents';
import { useSelector } from '../../utils/hooks';
import config from '../../config';

const FinePrint = styled(LinkTypography)`
  margin: 0 auto ${styles.spacing.extraTiny}px;
  font-size: 13px;
  line-height: 18px;
`;

const MarginBottomInput = styled(Input)`
  margin-bottom: ${styles.spacing.extraTiny}px;
`;

const StyledButton = styled(Button)`
  padding: ${styles.spacing.extraExtraTiny}px;
  margin: ${styles.spacing.medium}px auto;
  width: 100%;

  ${({ isLoading }) => isLoading && css`
    pointer-events: none;
  `}
`;
const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const CustomDiv = styled.div`
  margin: auto;
`;

const NativeLogin = ({
  onCreateNewAccount,
  onLoginSuccess,
}) => {
  const dispatch = useDispatch();
  const router = useRouter();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  // eslint-disable-next-line no-underscore-dangle
  const emailArrival = router.query._esk;
  const arrivalEmail = useSelector(getArrivalEmail);
  const profileClaimed = useSelector(isProfileKnown);
  const profileKnown = useSelector(isProfileClaimed);
  const isLoginLoader = useSelector(getIsLoginLoader);

  useEffect(() => {
    reportJobcaseSsoViewRendered(SSO_PAGE.LOGIN);
  }, []);

  /**
   * Pre-pop email only if the user is known, claimed and are coming from an email link
   */
  useEffect(() => {
    if (emailArrival && profileKnown && profileClaimed) {
      if (!arrivalEmail) {
        dispatch(actions.getProfileArrivalInfo());
      } else {
        setEmail(arrivalEmail);
      }
    }
  }, [emailArrival, arrivalEmail, profileKnown, profileClaimed]);

  const submit = async () => {
    // clear any previous errors
    dispatch({
      type: actions.TYPES.SET_LOGIN_ERROR,
      payload: null,
    });

    if (!isValidEmail(email)) {
      dispatch({
        type: actions.TYPES.SET_LOGIN_ERROR,
        payload: { login: 'Please enter a valid email address' },
      });
      reportJobcaseLoginNativeAuthError(AUTH_ERROR_REASON.EMAIL);
      return;
    }

    if (password.length < 1) {
      dispatch({
        type: actions.TYPES.SET_LOGIN_ERROR,
        payload: { login: 'Please enter a password' },
      });
      reportJobcaseLoginNativeAuthError(AUTH_ERROR_REASON.PASSWORD);
      return;
    }

    const arrivalKey = getArrivalKey();
    const loggedIn = await dispatch(attemptLogin({
      email,
      password,
    }, arrivalKey));
    if (loggedIn && onLoginSuccess) onLoginSuccess('Jobcase');
    reportJobcaseLoginNativeAuthClick();
  };

  // Only submit on enter key
  const checkForEnterKey = (e) => {
    if (e.key !== 'Enter') return;
    submit();
  };

  const handleEmailInput = e => setEmail(e.target.value);

  const handlePasswordInput = e => setPassword(e.target.value);

  return (
    <Wrapper>
      <MarginBottomInput
        placeholder="Email Address *"
        value={email}
        onChange={handleEmailInput}
        onKeyPress={checkForEnterKey}
      />
      <Input
        placeholder="Password *"
        value={password}
        onChange={handlePasswordInput}
        onKeyPress={checkForEnterKey}
        type="password"
      />
      <StyledButton
        noOuterPadding
        onClick={submit}
        isLoading={isLoginLoader}
      >
        {isLoginLoader ? <Spinner color={styles.color.jobcaseWhiteColor} /> : 'Log In'}
      </StyledButton>
      <CustomDiv>
        <FinePrint
          onClick={onCreateNewAccount}
        >
          Create A New Account
        </FinePrint>
      </CustomDiv>
      <CustomDiv>
        <FinePrint
          href={`${config.jobcaseUrl}/password/recover`}
          target="__blank"
          onClick={() => routeTo(`${config.jobcaseUrl}/password/recover`, false, null, true)}
        >
          Forgot Password?
        </FinePrint>
      </CustomDiv>
    </Wrapper>
  );
};

NativeLogin.propTypes = {
  onCreateNewAccount: PropTypes.func,
  onLoginSuccess: PropTypes.func,
};

NativeLogin.defaultProps = {
  onCreateNewAccount: () => {},
  onLoginSuccess: () => {},
};

export default NativeLogin;
